<script>
import { EditorState, Selection, TextSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { history } from 'prosemirror-history';
import { dropCursor } from 'prosemirror-dropcursor';
import { gapCursor } from 'prosemirror-gapcursor';

import {
  baseKeyMaps,
  blocksInputRule,
  linksInputRules,
  listInputRules,
} from '@chatwoot/prosemirror-schema/src/rules/index';
import Placeholder from '@chatwoot/prosemirror-schema/src/Placeholder';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';

import { createTypingIndicator } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  hasPressedCommandAndEnter,
  hasPressedEnterAndNotCmdOrShift,
} from 'shared/helpers/KeyboardHelpers';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import {
  getContentNode,
  insertAtCursor,
  scrollCursorIntoView,
} from 'dashboard/helper/editorHelper';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import CannedResponse from '../conversation/CannedResponse.vue';
import TagAgents from '../conversation/TagAgents.vue';
import VariableList from '../conversation/VariableList.vue';
import KeyboardEmojiSelector from '../WootWriter/keyboardEmojiSelector.vue';

import Toolbar from './Toolbar.vue';
import { setBlockType, toggleMark } from 'prosemirror-commands';

import {
  codeBlockReplacement,
  commandForTool,
  findLinkRange,
  findMarkRange,
  isMarkActive,
  isNodeActive,
  isToolEnabled,
  selectionSpansLines,
} from './commands';
import { MESSAGE_TOOLS, VARIABLE_TOOL } from './constants';
import { formattingKeymap } from './keymap';
import { blankLinePlugin } from './blankLines';
import { whatsappInputRules } from './inputRules';
import { resolveHref } from './links';
import { messageSchema, parse, serialize } from './markdown';

const TYPING_INDICATOR_IDLE_TIME = 4000;

export default {
  name: 'RichTextEditor',
  components: {
    CannedResponse,
    KeyboardEmojiSelector,
    TagAgents,
    Toolbar,
    VariableList,
  },
  mixins: [keyboardEventListenerMixins],
  props: {
    value: { type: String, default: '' },
    editorId: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    isPrivate: { type: Boolean, default: false },
    /** Ferramentas da barra, na ordem. Ver constants.js. */
    tools: { type: Array, default: () => [...MESSAGE_TOOLS] },
    enableSuggestions: { type: Boolean, default: true },
    enableVariables: { type: Boolean, default: false },
    enableCannedResponses: { type: Boolean, default: true },
    variables: { type: Object, default: () => ({}) },
    // Variaveis extras ({ label, key, description }) no picker de `{{` — as do
    // fluxo, quando o editor esta dentro do editor de automacoes. Vinha do
    // `WootWriter/Editor.vue`, que este componente substituiu.
    extraVariables: { type: Array, default: () => [] },
    // Liga o atalho de envio: a tecla configurada em "Enter envia" deixa de
    // quebrar a linha, para quem escuta la fora despachar a mensagem. So a
    // caixa de resposta da conversa envia por tecla, e so ela liga isto.
    //
    // Antes o atalho valia em TODO editor, porque a preferencia e global e o
    // componente a lia sozinho. Num formulario — agendamento, automacao,
    // assinatura — nao ha envio nenhum para a tecla disparar, entao o Enter
    // simplesmente nao fazia nada e sobrava o Shift+Enter para criar linha.
    enableSendHotkeys: { type: Boolean, default: false },
    updateSelectionWith: { type: String, default: '' },
    // `true` para acompanhar o editor antigo: as telas que dependiam do foco
    // automatico nao passavam a prop, entao mudar o default aqui as quebraria
    // em silencio.
    focusOnMount: { type: Boolean, default: true },
  },
  setup() {
    const { isEditorHotKeyEnabled } = useUISettings();
    return { isEditorHotKeyEnabled };
  },
  data() {
    return {
      editorView: null,
      state: null,
      // Espelha o state para que a barra recalcule os botoes a cada transacao;
      // o EditorState do ProseMirror e imutavel, entao a troca de referencia e
      // o que dispara a reatividade.
      toolbarState: null,
      typingIndicator: createTypingIndicator(
        () => this.$emit('typingOn'),
        () => this.$emit('typingOff'),
        TYPING_INDICATOR_IDLE_TIME
      ),
      range: null,
      showUserMentions: false,
      showCannedMenu: false,
      showVariables: false,
      showEmojiMenu: false,
      mentionSearchKey: '',
      cannedSearchTerm: '',
      variableSearchTerm: '',
      emojiSearchTerm: '',
      isLinkFormVisible: false,
      linkValue: '',
      linkError: false,
      // Trecho ao qual o link sera aplicado. Preenchido ao editar um link
      // existente; nulo quando o alvo e a selecao atual.
      linkRange: null,
    };
  },
  computed: {
    contentFromEditor() {
      return serialize(this.editorView.state.doc);
    },
    shouldShowVariables() {
      return this.enableVariables && this.showVariables && !this.isPrivate;
    },
    // A ferramenta de variável é acrescentada AQUI, e não pedida na prop
    // `tools`: assim nenhum consumidor precisa mudar, e ela não tem como
    // aparecer onde não há variável para inserir. A condição é a mesma do
    // picker de `{{` (skip: () => this.isPrivate) — nota privada não tem
    // variável, e um botão que abre o nada é pior que botão nenhum.
    toolbarTools() {
      if (!this.enableVariables || this.isPrivate) return this.tools;
      if (this.tools.includes(VARIABLE_TOOL)) return this.tools;
      return [...this.tools, VARIABLE_TOOL];
    },
    shouldShowCannedResponses() {
      return (
        this.enableCannedResponses && this.showCannedMenu && !this.isPrivate
      );
    },
    suggestionPlugins() {
      if (!this.enableSuggestions) return [];

      return [
        this.buildSuggestion('@', {
          onEnter: () => {
            this.showUserMentions = true;
          },
          onChange: text => {
            this.mentionSearchKey = text;
          },
          onExit: () => {
            this.mentionSearchKey = '';
            this.showUserMentions = false;
          },
          isOpen: () => this.showUserMentions,
        }),
        this.buildSuggestion('/', {
          skip: () => this.isPrivate,
          onEnter: () => {
            this.showCannedMenu = true;
          },
          onChange: text => {
            this.cannedSearchTerm = text;
          },
          onExit: () => {
            this.cannedSearchTerm = '';
            this.showCannedMenu = false;
          },
          isOpen: () => this.showCannedMenu,
        }),
        this.buildSuggestion('{{', {
          skip: () => this.isPrivate,
          onEnter: () => {
            this.showVariables = true;
          },
          onChange: text => {
            this.variableSearchTerm = text;
          },
          onExit: () => {
            this.variableSearchTerm = '';
            this.showVariables = false;
          },
          isOpen: () => this.showVariables,
        }),
        this.buildSuggestion(':', {
          minChars: 2,
          onEnter: text => {
            this.showEmojiMenu = true;
            this.emojiSearchTerm = text || '';
          },
          onChange: text => {
            this.emojiSearchTerm = text;
          },
          onExit: () => {
            this.emojiSearchTerm = '';
            this.showEmojiMenu = false;
          },
          isOpen: () => this.showEmojiMenu,
        }),
      ];
    },
  },
  watch: {
    value(newValue = '') {
      if (newValue !== this.contentFromEditor) {
        this.reloadState(newValue);
      }
    },
    editorId() {
      this.showCannedMenu = false;
      this.showEmojiMenu = false;
      this.showVariables = false;
      this.cannedSearchTerm = '';
      this.reloadState(this.value);
    },
    placeholder(newValue, oldValue) {
      if (newValue !== oldValue) this.reloadState(this.value);
    },
    isPrivate() {
      this.reloadState(this.value);
    },
    tools() {
      this.reloadState(this.value);
    },
    showUserMentions(value) {
      this.$emit('toggleUserMention', this.isPrivate && value);
    },
    showCannedMenu(value) {
      this.$emit('toggleCannedMenu', !this.isPrivate && value);
    },
    showVariables(value) {
      this.$emit('toggleVariablesMenu', !this.isPrivate && value);
    },
    updateSelectionWith(newValue, oldValue) {
      if (!this.editorView || newValue === oldValue || newValue === '') return;

      const node = this.editorView.state.schema.text(newValue);
      const tr = this.editorView.state.tr.replaceSelectionWith(node);

      this.editorView.focus();
      this.state = this.editorView.state.apply(tr);
      this.editorView.updateState(this.state);
      this.emitOnChange();
      this.$emit('clearSelection');
    },
  },
  created() {
    this.state = this.createState(this.value);
    // Ja no primeiro render, senao a barra aparece vazia ate o ciclo seguinte.
    this.syncToolbar();
  },
  mounted() {
    this.createEditorView();
    this.editorView.updateState(this.state);
    this.syncToolbar();

    if (this.focusOnMount) this.focusEditorInputField('end');

    this.$emitter.on(
      BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
      this.insertContentIntoEditor
    );
  },
  beforeDestroy() {
    this.$emitter.off(
      BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
      this.insertContentIntoEditor
    );
    document.removeEventListener('mousedown', this.closeLinkFormOnClickOutside);

    if (this.editorView) this.editorView.destroy();
  },
  methods: {
    buildSuggestion(character, handlers) {
      const {
        skip = () => false,
        onEnter,
        onChange,
        onExit,
        isOpen,
        minChars,
      } = handlers;

      return suggestionsPlugin({
        matcher: triggerCharacters(character, minChars),
        suggestionClass: character === '@' ? undefined : '',
        onEnter: args => {
          if (skip()) return false;
          this.range = args.range;
          this.editorView = args.view;
          onEnter(args.text);
          return false;
        },
        onChange: args => {
          this.range = args.range;
          this.editorView = args.view;
          onChange(args.text);
          return false;
        },
        onExit: () => {
          onExit();
          return false;
        },
        // Impede que o Enter que escolhe um item da lista tambem envie a
        // mensagem ou quebre a linha.
        onKeyDown: ({ event }) => event.keyCode === 13 && isOpen(),
      });
    },
    buildPlugins() {
      return [
        ...this.suggestionPlugins,
        history(),
        // Antes do keymap base: no ProseMirror o primeiro plugin que trata a
        // tecla vence, e e daqui que saem Ctrl+B e Ctrl+I.
        formattingKeymap(messageSchema),
        baseKeyMaps(messageSchema),
        // Depois dos keymaps: ele nao trata tecla, so devolve o documento a
        // forma de linhas depois que a tecla ja fez o que tinha que fazer.
        blankLinePlugin(messageSchema),
        blocksInputRule(messageSchema),
        whatsappInputRules(messageSchema),
        linksInputRules(messageSchema),
        listInputRules(messageSchema),
        dropCursor(),
        gapCursor(),
        Placeholder(this.placeholder),
      ];
    },
    createState(content) {
      return EditorState.create({
        doc: parse(content),
        plugins: this.buildPlugins(),
      });
    },
    createEditorView() {
      this.editorView = new EditorView(this.$refs.editor, {
        state: this.state,
        attributes: { class: 'rich-text-editor__content' },
        dispatchTransaction: tr => {
          this.state = this.state.apply(tr);
          this.editorView.updateState(this.state);
          this.syncToolbar();
          this.emitOnChange();
        },
        handleClick: (view, pos) => {
          // Clicar num trecho com link abre a caixa preenchida, para alterar
          // ou remover o endereco.
          const range = findMarkRange(
            view.state.doc.resolve(pos),
            view.state.schema.marks.link
          );

          if (range?.mark) {
            this.openLinkForm({
              href: range.mark.attrs.href,
              range,
            });
            return true;
          }

          if (this.isLinkFormVisible) this.closeLinkForm();

          return false;
        },
        handleDOMEvents: {
          keyup: () => this.onKeyup(),
          keydown: (view, event) => this.onKeydown(event),
          focus: () => this.$emit('focus'),
          blur: () => this.onBlur(),
          paste: (view, event) => {
            if (event.clipboardData?.files?.length > 0) event.preventDefault();
          },
        },
      });
    },
    reloadState(content = this.value) {
      this.state = this.createState(content);
      this.editorView.updateState(this.state);
      this.syncToolbar();
    },
    syncToolbar() {
      this.toolbarState = this.state;
    },
    emitOnChange() {
      this.$emit('input', this.contentFromEditor);
    },
    onKeyup() {
      this.typingIndicator.start();
    },
    onKeydown(event) {
      // Ctrl+B e Ctrl+I sao tratados pelo formattingKeymap, que aplica o mark
      // de verdade. O editor antigo interceptava aqui e inseria `*` e `_` como
      // texto, divergindo do que o botao da barra fazia.
      if (!this.enableSendHotkeys) return false;

      if (this.isEditorHotKeyEnabled('enter')) {
        this.preventLineBreak(event, hasPressedEnterAndNotCmdOrShift);
      }

      if (this.isEditorHotKeyEnabled('cmd_enter')) {
        this.preventLineBreak(event, hasPressedCommandAndEnter);
      }

      return false;
    },
    preventLineBreak(event, matches) {
      if (matches(event)) event.preventDefault();
    },
    onBlur() {
      this.typingIndicator.stop();
      this.$emit('blur');
    },
    getKeyboardEvents() {
      const focus = {
        action: () => this.focusEditorInputField(),
        allowOnFocusedInput: true,
      };

      return { 'Alt+KeyP': focus, 'Alt+KeyL': focus };
    },
    /**
     * Metodo publico: telas que trocam o conteudo por fora (uma resposta pronta
     * escolhida no painel inferior, por exemplo) devolvem o cursor ao texto.
     */
    focus(pos = 'end') {
      if (this.editorView) this.focusEditorInputField(pos);
    },
    focusEditorInputField(pos = 'end') {
      const { tr } = this.editorView.state;
      const selection =
        pos === 'end' ? Selection.atEnd(tr.doc) : Selection.atStart(tr.doc);

      this.editorView.dispatch(tr.setSelection(selection));
      this.editorView.focus();
    },
    insertSpecialContent(type, content) {
      if (!this.editorView) return;

      const { node, from, to } = getContentNode(
        this.editorView,
        type,
        content,
        this.range,
        this.variables
      );

      if (!node) return;

      this.insertNodeIntoEditor(node, from, to);

      this.$track(
        {
          mention: CONVERSATION_EVENTS.USED_MENTIONS,
          cannedResponse: CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE,
          variable: CONVERSATION_EVENTS.INSERTED_A_VARIABLE,
          emoji: CONVERSATION_EVENTS.INSERTED_AN_EMOJI,
        }[type]
      );
    },
    insertContentIntoEditor(content, defaultFrom = 0) {
      const from = defaultFrom || this.editorView.state.selection.from || 0;
      this.insertNodeIntoEditor(parse(content), from, undefined);
    },
    insertNodeIntoEditor(node, from = 0, to = 0) {
      this.state = insertAtCursor(this.editorView, node, from, to);
      this.syncToolbar();
      this.emitOnChange();
      this.$nextTick(() => scrollCursorIntoView(this.editorView));
    },
    /**
     * Executa a ferramenta escolhida na barra.
     *
     * Sempre sobre `editorView.state`, o estado vivo: o `editorState` que a
     * barra usa para desenhar so chega no ciclo seguinte a cada transacao, e
     * agir sobre ele faria um clique logo apos mudar a selecao operar na
     * selecao anterior.
     */
    // Insere o gatilho `{{` no cursor e deixa o plugin de sugestao abrir a
    // lista. Nao existe picker proprio: o `apply` do plugin roda a CADA
    // transacao e casa pelo texto antes do cursor, nao por evento de tecla —
    // entao inserir por codigo tem o mesmo efeito de digitar (issue #569).
    //
    // O espaco antes NAO e enfeite. O matcher (`triggerCharacters`) so aceita o
    // gatilho no inicio do texto ou depois de espaco; colado num caractere
    // qualquer ele nao casa, e o botao inseriria `{{` sem abrir lista nenhuma.
    // Digitar tem exatamente a mesma regra, entao isto nao inventa
    // comportamento novo — so o cumpre.
    insertVariableTrigger(state) {
      // Ancora no FIM da selecao, nao no inicio: com texto selecionado, inserir
      // no intervalo o substituiria e o texto do usuario sumiria. E estado
      // normal aqui -- a barra preserva a selecao de proposito
      // (`@mousedown.prevent`), e o botao de link ao lado so existe QUANDO ha
      // selecao. Trocar texto selecionado por `{{` seria destrutivo e sem
      // desfazer obvio para quem clicou querendo inserir.
      const { to } = state.selection;
      const anterior = state.doc.textBetween(
        Math.max(0, to - 1),
        to,
        '\0',
        '\0'
      );
      const prefixo = anterior === '' || /^[\s\0]$/.test(anterior) ? '' : ' ';
      const texto = `${prefixo}{{`;

      const tr = state.tr.insertText(texto, to, to);
      // Cursor logo depois do `{{`: e a posicao que o matcher le, e tambem onde
      // o usuario vai digitar para filtrar a lista.
      const destino = to + texto.length;
      tr.setSelection(TextSelection.create(tr.doc, destino));

      this.editorView.dispatch(tr);
      this.editorView.focus();
    },
    runTool(tool) {
      if (!this.editorView) return;

      const { state } = this.editorView;

      if (!isToolEnabled(tool, state)) return;

      if (tool.action === 'link') {
        this.toggleLink(state);
        return;
      }

      if (tool.action === 'clear') {
        this.clearContent(state);
        return;
      }

      if (tool.action === 'code') {
        this.toggleCode(state);
        return;
      }

      if (tool.action === 'variable') {
        this.insertVariableTrigger(state);
        return;
      }

      const command = commandForTool(tool, state.schema);

      if (command) {
        command(state, transaction => this.editorView.dispatch(transaction));
      }

      // Sem isso o foco fica na barra e o usuario precisa clicar de volta no
      // texto para continuar escrevendo.
      this.editorView.focus();
    },
    /**
     * Liga/desliga codigo, escolhendo entre as duas formas do WhatsApp.
     *
     * Uma linha vira `codigo` (mark inline, crase simples). Selecao que cruza
     * mais de um bloco vira `code_block` (tres crases). A troca automatica de
     * inline para bloco quando o texto ganha uma quebra fica no keymap, porque
     * acontece ao teclar Enter e nao ao clicar no botao.
     */
    toggleCode(state) {
      const { schema } = state;
      const dispatch = transaction => this.editorView.dispatch(transaction);

      if (isNodeActive(state, schema.nodes.code_block)) {
        setBlockType(schema.nodes.paragraph)(state, dispatch);
        this.editorView.focus();
        return;
      }

      if (isMarkActive(state, schema.marks.code)) {
        toggleMark(schema.marks.code)(state, dispatch);
        this.editorView.focus();
        return;
      }

      // A forma e decidida agora, no momento de aplicar, e nao muda depois:
      // editar o texto nao converte um no outro.
      if (selectionSpansLines(state)) {
        this.applyCodeBlock(state);
      } else {
        toggleMark(schema.marks.code)(state, dispatch);
      }

      this.editorView.focus();
    },
    /**
     * Troca o trecho selecionado por um bloco de codigo.
     *
     * `code_block` so aceita texto, entao as quebras viram `\n` de verdade —
     * um `setBlockType` direto descartaria os `hard_break` e juntaria tudo
     * numa linha.
     *
     * A troca e feita sobre as linhas inteiras que a selecao toca (ver
     * `codeBlockReplacement`), e o que sobrou de fora volta em volta do bloco.
     * Deixar o ProseMirror encaixar o bloco no intervalo cru abria uma linha
     * em branco acima dele.
     *
     * @param {EditorState} state
     */
    applyCodeBlock(state) {
      const { from, to } = state.selection;
      const texto = state.doc.textBetween(from, to, '\n', '\n');
      const { code_block: codeBlock } = state.schema.nodes;

      const bloco = codeBlock.create(
        null,
        texto ? state.schema.text(texto) : null
      );

      const troca = codeBlockReplacement(state, bloco);

      // Sem linhas inteiras para trocar resta o intervalo cru, e o ProseMirror
      // acomoda o bloco como der.
      if (!troca) {
        this.editorView.dispatch(state.tr.replaceRangeWith(from, to, bloco));
        return;
      }

      const tr = state.tr.replaceWith(troca.from, troca.to, troca.nodes);

      // A selecao segue sobre o mesmo texto, agora dentro do bloco — como
      // acontece com o codigo inline, onde `toggleMark` a preserva.
      tr.setSelection(
        TextSelection.create(
          tr.doc,
          troca.blockPos + 1,
          troca.blockPos + bloco.nodeSize - 1
        )
      );

      this.editorView.dispatch(tr);
    },
    /**
     * Apaga tudo o que foi digitado.
     *
     * E uma transacao comum, e nao um `reloadState`: assim entra no historico
     * e o Ctrl+Z devolve o texto. Sem isso, um clique acidental seria
     * irreversivel.
     */
    clearContent(state) {
      // Sem `scrollIntoView`: o documento fica vazio, nao ha para onde rolar.
      this.editorView.dispatch(state.tr.delete(0, state.doc.content.size));
      this.editorView.focus();
    },
    toggleLink(state) {
      const range = findLinkRange(state);

      // Com link ja aplicado o botao remove; caso contrario pede o endereco.
      if (range?.mark) {
        this.removeLink(range);
        return;
      }

      this.openLinkForm();
    },
    /**
     * Remove o link do trecho inteiro.
     *
     * `toggleMark` nao servia aqui: com o cursor parado dentro do link a
     * selecao esta vazia, e ele so mexe nos stored marks — o texto marcado
     * ficava do jeito que estava, e o botao parecia nao fazer nada.
     */
    removeLink(range = findLinkRange(this.editorView.state)) {
      if (!range) return;

      const { state } = this.editorView;

      this.editorView.dispatch(
        state.tr.removeMark(range.from, range.to, state.schema.marks.link)
      );
      this.closeLinkForm();
    },
    openLinkForm({ href = '', range = null } = {}) {
      this.linkValue = href;
      this.linkRange = range;
      this.linkError = false;
      this.isLinkFormVisible = true;

      // O prompt antigo fechava ao clicar fora dele; sem isso o campo fica
      // aberto e o unico jeito de sair e o Esc.
      document.addEventListener('mousedown', this.closeLinkFormOnClickOutside);

      this.$nextTick(() => this.$refs.linkInput?.focus());
    },
    closeLinkFormOnClickOutside(event) {
      if (!this.$refs.linkForm?.contains(event.target)) {
        this.closeLinkForm();
      }
    },
    closeLinkForm() {
      document.removeEventListener(
        'mousedown',
        this.closeLinkFormOnClickOutside
      );
      this.isLinkFormVisible = false;
      this.linkValue = '';
      this.linkRange = null;
      this.linkError = false;
      this.editorView.focus();
    },
    applyLink() {
      const href = resolveHref(this.linkValue);

      if (!href) {
        // Vazio, texto que nao e endereco ou esquema perigoso caem aqui. O
        // prompt antigo so sinalizava o caso vazio, e gravava o resto cru.
        this.linkError = true;
        return;
      }

      const { state } = this.editorView;
      const { from, to } = this.linkRange ?? state.selection;
      const markType = state.schema.marks.link;

      // Remove antes de aplicar: ao editar um link existente, sobrepor sem
      // limpar deixaria os dois endereços no mesmo trecho.
      this.editorView.dispatch(
        state.tr
          .removeMark(from, to, markType)
          .addMark(from, to, markType.create({ href }))
      );

      this.closeLinkForm();
    },
  },
};
</script>

<template>
  <div ref="editorRoot" class="rich-text-editor">
    <TagAgents
      v-if="showUserMentions && isPrivate"
      :search-key="mentionSearchKey"
      @click="content => insertSpecialContent('mention', content)"
    />
    <CannedResponse
      v-if="shouldShowCannedResponses"
      :search-key="cannedSearchTerm"
      @click="content => insertSpecialContent('cannedResponse', content)"
    />
    <VariableList
      v-if="shouldShowVariables"
      :search-key="variableSearchTerm"
      :extra-variables="extraVariables"
      @click="content => insertSpecialContent('variable', content)"
    />
    <KeyboardEmojiSelector
      v-if="showEmojiMenu"
      :search-key="emojiSearchTerm"
      @click="emoji => insertSpecialContent('emoji', emoji)"
    />

    <Toolbar
      :editor-view="editorView"
      :editor-state="toolbarState"
      :tools="toolbarTools"
      @run="runTool"
    />

    <div
      v-if="isLinkFormVisible"
      ref="linkForm"
      class="rich-text-editor__link-form"
    >
      <input
        ref="linkInput"
        v-model="linkValue"
        type="url"
        class="rich-text-editor__link-input"
        :class="{ 'has-error': linkError }"
        :placeholder="$t('RICH_TEXT_EDITOR.LINK_PROMPT')"
        :aria-invalid="linkError ? 'true' : 'false'"
        :aria-describedby="linkError ? 'rich-text-editor-link-error' : null"
        @input="linkError = false"
        @keydown.enter.prevent="applyLink"
        @keydown.esc.prevent="closeLinkForm"
      />
      <button
        type="button"
        class="rich-text-editor__link-action"
        @mousedown.prevent
        @click="applyLink"
      >
        {{ $t('RICH_TEXT_EDITOR.LINK_CONFIRM') }}
      </button>
      <button
        v-if="linkRange"
        type="button"
        class="rich-text-editor__link-action is-danger"
        @mousedown.prevent
        @click="removeLink(linkRange)"
      >
        {{ $t('RICH_TEXT_EDITOR.LINK_REMOVE') }}
      </button>
      <button
        type="button"
        class="rich-text-editor__link-action is-secondary"
        @mousedown.prevent
        @click="closeLinkForm"
      >
        {{ $t('RICH_TEXT_EDITOR.LINK_CANCEL') }}
      </button>

      <!--
        A borda vermelha sozinha nao diz o motivo, e nao chega a quem usa leitor
        de tela: sem isto o clique em Inserir deixava o formulario aberto e nada
        era anunciado. `role="alert"` faz o texto ser lido assim que aparece.
      -->
      <p
        v-if="linkError"
        id="rich-text-editor-link-error"
        role="alert"
        class="rich-text-editor__link-error"
      >
        {{ $t('RICH_TEXT_EDITOR.LINK_ERROR') }}
      </p>
    </div>

    <div ref="editor" />

    <slot name="footer" />
  </div>
</template>

<style lang="scss">
@import '~@chatwoot/prosemirror-schema/src/styles/base.scss';

.rich-text-editor {
  @apply w-full relative flex flex-col;

  .rich-text-editor__content {
    // `font-normal` explicito: em tres telas de agendamento o editor fica
    // dentro de um `<label>`, e o `font-medium` global do label vazava para o
    // texto. O resultado parecia negrito ligado desde o inicio — e clicar em B
    // nao desligava, porque o peso nao vinha de mark nenhum.
    // Respiro de 8px no topo: sem ele a primeira linha encosta na regua da
    // barra de ferramentas. Os outros lados ficam zerados — o recuo horizontal
    // e do container, e repeti-lo aqui somaria aos dois.
    @apply pt-2 px-0 pb-0 break-words font-normal text-body overflow-auto
      min-h-[5rem] max-h-[7.5rem] outline-none;

    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      @apply text-body;
    }

    blockquote {
      @apply border-line-strong;

      p {
        @apply text-subtle;
      }
    }

    // Um paragrafo aqui e uma LINHA, e nao um bloco espacado.
    //
    // O vao entre dois blocos existe, mas nao e margem: e um paragrafo vazio de
    // verdade, posto por `blankLinePlugin`. Desenha-lo com margem acertava a
    // aparencia e mentia no resto — margem nao e posicao no documento, entao o
    // cursor nao entrava nela e a seta pulava de um paragrafo direto para o
    // outro, como se a linha em branco nao existisse.
    //
    p {
      @apply m-0;
    }

    // A lista, ao contrario, e bloco de verdade, e a fronteira em volta dela
    // vale uma linha em branco na mensagem entregue — `texto\n\n- a\n\nfim`.
    // Sem margem o editor desenhava tudo colado e mentia sobre esse vao. Vale
    // exatamente uma linha, como o `line-height` do ProseMirror.
    ul,
    ol {
      @apply my-[1.4em] mx-0;
    }

    // Sublista nao ganha vao: o WhatsApp nao tem sublista e ela chega achatada.
    li ul,
    li ol {
      @apply m-0;
    }

    // Sem isto sobra um vao morto nas pontas da area de edicao.
    > :first-child {
      @apply mt-0;
    }

    > :last-child {
      @apply mb-0;
    }

    ol li {
      @apply list-item list-decimal;
    }

    ul li {
      @apply list-item list-disc;
    }
  }

  .prosemirror-mention-node {
    @apply font-medium bg-yellow-100 dark:bg-yellow-800 text-body py-0 px-1;
  }
}

.rich-text-editor__link-form {
  // `wrap` para a mensagem de erro cair na linha de baixo em vez de espremer o
  // campo e os botoes.
  @apply flex flex-wrap items-center gap-2 py-1;
}

.rich-text-editor__link-error {
  @apply basis-full m-0 text-sm text-red-400;
}

.rich-text-editor__link-input {
  @apply flex-1 min-w-0 text-sm rounded border border-solid border-line
    bg-surface text-body px-2 py-1;

  &.has-error {
    @apply border-red-400;
  }
}

.rich-text-editor__link-action {
  @apply text-sm rounded px-2 py-1 border-0 cursor-pointer bg-surface-hover
    text-body;

  &.is-secondary {
    @apply bg-transparent text-muted;
  }

  &.is-danger {
    @apply bg-transparent text-red-500;
  }
}
</style>
